import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  DEFAULT_OG_IMAGE,
  DEFAULT_OG_IMAGE_ALT,
  DEFAULT_OG_IMAGE_HEIGHT,
  DEFAULT_OG_IMAGE_WIDTH,
  PAGE_DESCRIPTIONS,
  PROJECT_OG_IMAGES,
  PROJECT_THUMBNAIL_HEIGHT,
  PROJECT_THUMBNAIL_WIDTH,
  SITE_AUTHOR,
  SITE_LOCALE,
  SITE_NAME,
  SITE_URL,
} from "../data/seoMeta";

const formatTitle = (page) => `${SITE_NAME} | ${page}`;

const PAGE_TITLES = {
  "/": SITE_NAME,
  "/projects": formatTitle("Projects & Case Studies"),
  "/portfolio-pdf": formatTitle("Portfolio PDF"),
  "/about": formatTitle("About Nadine"),
  "/contact": formatTitle("Contact"),
  "/terms": formatTitle("Terms of Service"),
  "/privacy": formatTitle("Privacy Policy"),
  "/projects/aesthetic-art-gallery": formatTitle("Aesthetic Art Gallery"),
  "/projects/custom-pc-builder": formatTitle("Custom PC Builder"),
  "/projects/funzies-collection": formatTitle("Funzies Collection"),
  "/projects/icook": formatTitle("iCook"),
  "/projects/lifecanvas": formatTitle("LifeCanvas"),
  "/projects/soundart": formatTitle("SoundArt"),
  "/projects/eversio": formatTitle("Project Eversio"),
};

const NOT_FOUND_TITLE = formatTitle("404 - Page Not Found");
const NOT_FOUND_DESCRIPTION =
  "The page you requested could not be found on the Avida2C portfolio. Return to the homepage or browse projects.";
const DEFAULT_DESCRIPTION = PAGE_DESCRIPTIONS["/"];

const getPageTitle = (pathname) => PAGE_TITLES[pathname] ?? NOT_FOUND_TITLE;

const getPageDescription = (pathname) =>
  PAGE_DESCRIPTIONS[pathname] ??
  (PAGE_TITLES[pathname] ? DEFAULT_DESCRIPTION : NOT_FOUND_DESCRIPTION);

const getOgImage = (pathname) => PROJECT_OG_IMAGES[pathname] ?? DEFAULT_OG_IMAGE;

const getOgImageAlt = (pathname, title) =>
  PROJECT_OG_IMAGES[pathname]
    ? `${title} — project case study thumbnail`
    : DEFAULT_OG_IMAGE_ALT;

const getOgImageDimensions = (pathname) =>
  PROJECT_OG_IMAGES[pathname]
    ? {
        width: PROJECT_THUMBNAIL_WIDTH,
        height: PROJECT_THUMBNAIL_HEIGHT,
      }
    : {
        width: DEFAULT_OG_IMAGE_WIDTH,
        height: DEFAULT_OG_IMAGE_HEIGHT,
      };

const isKnownRoute = (pathname) => pathname in PAGE_TITLES;

const setMetaContent = (attribute, key, content) => {
  const selector =
    attribute === "property"
      ? `meta[property="${key}"]`
      : `meta[name="${key}"]`;
  let meta = document.querySelector(selector);

  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute(attribute, key);
    document.head.appendChild(meta);
  }

  meta.setAttribute("content", content);
};

const setCanonicalUrl = (url) => {
  let link = document.querySelector('link[rel="canonical"]');

  if (!link) {
    link = document.createElement("link");
    link.rel = "canonical";
    document.head.appendChild(link);
  }

  link.href = url;
};

const removeCanonicalUrl = () => {
  document.querySelector('link[rel="canonical"]')?.remove();
};

const removeRobotsMeta = () => {
  document.querySelector('meta[name="robots"]')?.remove();
};

const updatePageMeta = (pathname, title, description) => {
  const url = `${SITE_URL}${pathname}`;
  const image = getOgImage(pathname);
  const imageAlt = getOgImageAlt(pathname, title);
  const { width: imageWidth, height: imageHeight } =
    getOgImageDimensions(pathname);
  const notFound = !isKnownRoute(pathname);

  document.title = title;
  setMetaContent("name", "description", description);
  setMetaContent("name", "author", SITE_AUTHOR);
  setMetaContent("property", "og:type", "website");
  setMetaContent("property", "og:locale", SITE_LOCALE);
  setMetaContent("property", "og:site_name", SITE_NAME);
  setMetaContent("property", "og:title", title);
  setMetaContent("property", "og:description", description);
  setMetaContent("property", "og:url", url);
  setMetaContent("property", "og:image", image);
  setMetaContent("property", "og:image:alt", imageAlt);
  setMetaContent("property", "og:image:width", String(imageWidth));
  setMetaContent("property", "og:image:height", String(imageHeight));
  setMetaContent("name", "twitter:card", "summary_large_image");
  setMetaContent("name", "twitter:title", title);
  setMetaContent("name", "twitter:description", description);
  setMetaContent("name", "twitter:image", image);
  setMetaContent("name", "twitter:image:alt", imageAlt);

  if (notFound) {
    removeCanonicalUrl();
    setMetaContent("name", "robots", "noindex, nofollow");
  } else {
    setCanonicalUrl(url);
    removeRobotsMeta();
  }
};

const DynamicTitle = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    updatePageMeta(pathname, getPageTitle(pathname), getPageDescription(pathname));
  }, [pathname]);

  return null;
};

export default DynamicTitle;
